<template>
  <cf-validated-form
    name="upsellMenuItem"
    :disabled="isAddingItem"
    @valid-submit="upsellItemSubmitHandler"
  >
    <!-- Images -->
    <image-scroller
      v-if="images.length > 0"
      :images="images"
    />

    <div :class="['cf-flex cf-flex--column cf-flex--gap-16', { 'cf-margin-top-16': images.length > 0 }]">
      <div class="cf-flex cf-flex--column cf-flex--gap-20 cf-text-align-left cf-modal__subheader cf-modal__subheader--sticky">
        <div class="cf-flex cf-flex--column cf-flex--gap-8">
          <!-- Item Name -->
          <h2 class="cf-title cf-title--2">
            {{ upsellMenuItem.displayName }}
          </h2>

          <!-- Item Description -->
          <v-clamp
            v-if="upsellMenuItem.descriptionText"
            tag="p"
            class="cf-subtitle cf-subtitle--2 cf-subtitle--black"
            autoresize
            :max-lines="10"
          >
            <template #default>
              {{ upsellMenuItem.descriptionText }}
            </template>

            <template #after="{ clamped, expanded, toggle }">
              <template v-if="clamped || expanded">
                <br>

                <button
                  class="cf-link cf-link--brand-secondary cf-link--heavy"
                  type="button"
                  @click="toggle"
                >
                  {{ clamped ? 'Read More' : 'Read Less' }}
                </button>
              </template>
            </template>
          </v-clamp>
        </div>

        <!-- Dietary Attributes -->
        <div
          v-if="dietaryAttributes.length > 0"
          class="cf-flex cf-flex--row cf-flex--gap-16"
        >
          <div
            v-if="dietaryAttributes.length > 0"
            class="cf-flex cf-flex--row cf-flex--wrap cf-flex--gap-16"
          >
            <div
              v-for="attribute in dietaryAttributes"
              :key="attribute.name"
              class="cf-flex cf-flex--row cf-flex--wrap cf-flex--align-center cf-flex--gap-8"
            >
              <img
                class="cf-image-icon cf-image--24"
                :src="attribute.imageUrl"
                :alt="attribute.name"
              >

              <p class="cf-subtitle cf-subtitle--2">
                {{ attribute.name }}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="upsellMenuItem.modifierGroups.length > 0"
        class="cf-flex cf-flex--column"
      >
        <!-- Modifier Groups -->
        <cf-modifier-groups
          :groups="upsellMenuItem.modifierGroups"
          :hide-prices="selectedLocation.features.HidePrices"
          :selected-modifiers="selectedModifiers"
          @selected="modifiersSelectedHandler"
        />
      </div>

      <div class="cf-modal__footer cf-modal__footer--row cf-modal__footer--sticky">
        <!-- Quantity Input -->
        <cf-quantity-input
          v-model="quantity"
          :max-value="maxValue"
          name="menuItemQuantity"
        />

        <!-- Add To Upsell Temporary Cart Button -->
        <button
          :class="['cf-button cf-button--primary cf-button--lg cf-button--auto', { 'cf-button--loading': isAddingItem }]"
          type="submit"
        >
          Add
          <template v-if="!selectedLocation.features.HidePrices">
            {{ $dollars(priceWithModifiers) }}
          </template>
        </button>
      </div>
    </div>
  </cf-validated-form>
</template>


<script>
  import { mapGetters } from 'vuex';

  import calculatePriceWithModifiers from '@/helpers/calculate-price-with-modifiers';

  import kebabcase from '@/filters/kebabcase';

  import CfQuantityInput from '@/view/components/shared/cf-quantity-input/index.vue';
  import CfValidatedForm from '@/view/components/shared/cf-validated-form/index.vue';


  export default {
    name: 'UpsellItemDetails',

    components: {
      CfQuantityInput,
      CfValidatedForm
    },

    props: {
      upsellMenuItem: {
        type: Object,
        required: true
      }
    },

    data() {
      return {
        quantity: 1,
        modifiers: [],
        isAddingItem: false
      };
    },

    computed: {
      ...mapGetters('locations', ['selectedLocation']),
      ...mapGetters('merchant', ['merchant']),
      ...mapGetters('orders', ['order', 'orderValidations']),

      dietaryAttributes() {
        return this.upsellMenuItem.attributes
          ? this.upsellMenuItem.attributes
            .filter(attribute => ['Allergen', 'FoodType', 'SpiceLevel'].includes(attribute.type))
            .map(attribute => ({ ...attribute, iconName: kebabcase(attribute.name) }))
          : [];
      },

      images() {
        const images = [];

        if (this.upsellMenuItem.images) {
          this.upsellMenuItem.images.forEach((image) => {
            if (image.label && image.label.indexOf('itemDetails') === 0) {
              images.push({
                altText: this.upsellMenuItem.displayName,
                source: image.url
              });
            }
          });
        }

        return images;
      },

      priceWithModifiers() {
        return calculatePriceWithModifiers(this.upsellMenuItem, this.modifiers) * this.quantity;
      },

      selectedModifiers() {
        if (this.upsellMenuItem) {
          return this.upsellMenuItem.modifiers;
        }

        return null;
      },

      maxValue() {
        const { orderMaximumItemCount } = this.orderValidations.validations;

        const isUnlimitedItem = this.upsellMenuItem.maxAllowed === -1;

        const itemMax = this.upsellMenuItem.maxAllowed;

        if (orderMaximumItemCount) {
          // TODO: Verify if we want to limit max to include other selected upsell items
          const cartMax = Math.max(orderMaximumItemCount - (this.order.orderItemCount + this.quantity), 0);

          return isUnlimitedItem ? cartMax : Math.min(itemMax, cartMax);
        }

        return itemMax;
      }
    },

    methods: {
      modifiersSelectedHandler(modifiers) {
        this.modifiers = modifiers;
      },

      upsellItemSubmitHandler() {
        this.isAddingItem = true;

        const upsellItemPayload = { quantity: this.quantity, modifiers: this.modifiers };

        this.$emit('update-upsell-item-data', upsellItemPayload);

        this.isAddingItem = false;

        this.$closeModal('upsellItemDetailsModal');
      }
    }
  };
</script>
